// frontend/src/features/import/components/TemplatesMigrationDialog.tsx
//
// One-time prompt that offers to upload a user's legacy localStorage
// templates (Tier 1) to the tenant-scoped backend (Tier 2). Fires only
// when both of the following are true:
//
//   1. The legacy localStorage slot has at least one template
//   2. The migration sentinel key is not 'migrated' or 'dismissed'
//
// User actions:
//   - Migrate: sequentially POSTs each template, removes each from
//     localStorage on success. Partial failures leave the failed
//     ones in localStorage and keep the sentinel as 'pending' so the
//     dialog returns next session. Full success sets 'migrated' and
//     clears everything.
//   - Keep private: sets 'dismissed' so the dialog doesn't re-prompt.
//     Templates stay in localStorage but are invisible to the
//     tenant-backed hook.
//   - Close (X / Escape): same as Keep private, except the sentinel
//     stays 'pending' so the dialog can reappear later.
import { useCallback, useEffect, useState, type JSX } from 'react'
import { AlertTriangle, Star, X } from 'lucide-react'
import { useCustomTemplates, DuplicateTemplateNameError } from '../hooks/useCustomTemplates'
import {
  clearLegacyTemplates,
  getMigrationState,
  loadLegacyTemplates,
  removeLegacyTemplate,
  setMigrationState,
} from '../utils/legacy-template-storage'
import type { CustomTemplate } from '@shared/custom-templates/types'
import { cn } from '@/lib/utils'

interface MigrationFailure {
  name: string
  reason: string
}

export function TemplatesMigrationDialog(): JSX.Element | null {
  const { save } = useCustomTemplates()
  const [legacy, setLegacy] = useState<CustomTemplate[]>([])
  const [open, setOpen] = useState(false)
  const [isMigrating, setIsMigrating] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [failures, setFailures] = useState<MigrationFailure[]>([])

  // On mount, decide whether to show the dialog. We don't react to
  // localStorage changes at runtime — this is a one-shot on app load.
  useEffect((): void => {
    const state = getMigrationState()
    if (state === 'migrated' || state === 'dismissed') return
    const rows = loadLegacyTemplates()
    if (rows.length === 0) return
    setLegacy(rows)
    setOpen(true)
  }, [])

  const handleMigrate = useCallback(async (): Promise<void> => {
    setIsMigrating(true)
    setFailures([])
    const fails: MigrationFailure[] = []
    let successCount = 0

    // Sequential — parallel would be faster but harder to reason about
    // for partial failure. Volume is small (< 100 templates), latency
    // is dominated by network RTT, so sequential is fine.
    for (let i = 0; i < legacy.length; i++) {
      const tpl = legacy[i]!
      setProgress({ done: i, total: legacy.length })
      try {
        await save({
          name: tpl.name,
          ...(tpl.description ? { description: tpl.description } : {}),
          payload: tpl.payload,
        })
        removeLegacyTemplate(tpl.id)
        successCount += 1
      } catch (err) {
        const reason =
          err instanceof DuplicateTemplateNameError
            ? 'A template with this name already exists in your team.'
            : err instanceof Error
              ? err.message
              : 'Unknown error'
        fails.push({ name: tpl.name, reason })
      }
    }
    setProgress({ done: legacy.length, total: legacy.length })
    setIsMigrating(false)

    if (fails.length === 0) {
      // Full success — wipe legacy storage entirely and close.
      clearLegacyTemplates()
      setMigrationState('migrated')
      setOpen(false)
      return
    }

    // Partial success — show the failures inline. Leave sentinel at
    // 'pending' so the dialog returns next session and the user can
    // retry. Refresh the list to drop the ones that did migrate.
    setFailures(fails)
    setLegacy(loadLegacyTemplates())
    if (successCount > 0) {
      // Force a refresh of the legacy array we're displaying.
    }
  }, [legacy, save])

  const handleKeepPrivate = useCallback((): void => {
    setMigrationState('dismissed')
    setOpen(false)
  }, [])

  const handleClose = useCallback((): void => {
    // Close without changing the sentinel — dialog will reappear next
    // session. Matches a user who just wants to think about it.
    setOpen(false)
  }, [])

  if (!open || legacy.length === 0) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-[520px] rounded-lg border border-[var(--import-border)] bg-[var(--import-bg-secondary)] shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--import-border)] px-4 py-3">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-[var(--import-text-primary)]">
            <Star className="h-3.5 w-3.5 text-amber-400" />
            Share templates with your team?
          </h3>
          <button
            onClick={handleClose}
            disabled={isMigrating}
            className="text-[var(--import-text-muted)] hover:text-[var(--import-text-primary)] disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-3 px-4 py-4">
          <p className="text-xs text-[var(--import-text-secondary)]">
            You have {legacy.length} custom template{legacy.length === 1 ? '' : 's'} saved
            on this device from the older version of PIPE-FLO Connect. Want to move
            {legacy.length === 1 ? ' it ' : ' them '}
            to your team's shared library so everyone in your organization can use
            {legacy.length === 1 ? ' it' : ' them'}?
          </p>

          {/* List of templates to migrate */}
          <div className="max-h-[180px] overflow-y-auto rounded border border-[var(--import-border)] bg-[var(--import-bg-tertiary)] p-2">
            {legacy.map((tpl): JSX.Element => (
              <div
                key={tpl.id}
                className="flex items-center gap-2 py-1 text-xs text-[var(--import-text-primary)]"
              >
                <Star className="h-3 w-3 shrink-0 text-amber-400" />
                <span className="flex-1 truncate">{tpl.name}</span>
                {tpl.description && (
                  <span className="truncate text-[10px] text-[var(--import-text-muted)]">
                    {tpl.description}
                  </span>
                )}
              </div>
            ))}
          </div>

          {progress && isMigrating && (
            <div className="text-[11px] text-[var(--import-text-muted)]">
              Migrating {progress.done} of {progress.total}…
            </div>
          )}

          {failures.length > 0 && (
            <div className="space-y-1 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-400">
              <div className="flex items-center gap-1.5 font-medium">
                <AlertTriangle className="h-3 w-3" />
                {failures.length} template{failures.length === 1 ? '' : 's'} couldn't be migrated
              </div>
              <ul className="ml-4 list-disc">
                {failures.map((f): JSX.Element => (
                  <li key={f.name}>
                    <span className="font-medium">{f.name}</span>: {f.reason}
                  </li>
                ))}
              </ul>
              <div className="pt-1 text-[10px] opacity-80">
                These stayed on your device. You can retry later or rename them to avoid conflicts.
              </div>
            </div>
          )}

          <p className="text-[10px] text-[var(--import-text-muted)]">
            "Keep private" leaves your templates on this device only — they won't
            be visible to teammates, and you'll need to recreate them on other machines.
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-[var(--import-border)] px-4 py-3">
          <button
            onClick={handleKeepPrivate}
            disabled={isMigrating}
            className="rounded px-3 py-1.5 text-xs text-[var(--import-text-secondary)] hover:bg-[var(--import-border)] disabled:opacity-40"
          >
            Keep private
          </button>
          <button
            onClick={() => void handleMigrate()}
            disabled={isMigrating || legacy.length === 0}
            className={cn(
              'rounded-md bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white',
              'hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40',
            )}
          >
            {isMigrating ? 'Migrating…' : `Share ${legacy.length === 1 ? '1 template' : `${legacy.length} templates`} with team`}
          </button>
        </div>
      </div>
    </div>
  )
}
